// src/components/layout/HeaderActionButtons.jsx
/* ========================
 * HeaderActionButtons - Action buttons for the header
 * Mobile-optimized to hide unnecessary panel buttons and show only essential actions
 * ========================*/
import React from 'react';
import ReactDOM from 'react-dom';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Bookmark, Mail, Palette, PlusIcon, Power } from 'lucide-react';
import PruningSlider from '@/components/layout/RightPanel/PruningSlider';
import { authManager } from '@/managers/AuthManager';
import { useTheme } from '@/managers/ThemeProvider';
import WorkspaceSwitcher from './WorkspaceSwitcher';

const TOOLTIP_DELAY = 200;

/* ========================
 * TooltipButton - Generic button with Radix Tooltip
 * Reusable component for header actions with theme awareness
 * ========================*/
const TooltipButton = React.memo(React.forwardRef(({
  onClick,
  tooltipText,
  icon: Icon,
  isActive,
  className,
  iconClassName,
}, ref) => {
  const { isLightTheme } = useTheme();
  
  return (
    <div className="w-6 flex justify-center">
      <Tooltip.Root delayDuration={TOOLTIP_DELAY}>
        <Tooltip.Trigger asChild>
          <button
            ref={ref}
            onClick={onClick}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-150
              ${isLightTheme 
                ? `hover:bg-white/20 ${isActive ? 'bg-white/30' : ''}`
                : `hover:bg-surface-dark ${isActive ? 'bg-surface-dark' : ''}`
              } ${className || ''}`}
          >
            {Icon && <Icon className={`w-4 h-4 transition-colors ${isLightTheme ? 'text-white' : 'text-on-surface'} ${iconClassName || ''}`} />}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="bg-surface-dark px-3 py-1.5 rounded-md text-xs text-on-surface z-50"
            sideOffset={5}
          >
            {tooltipText}
            <Tooltip.Arrow className="fill-surface-dark" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </div>
  );
}));

TooltipButton.displayName = 'TooltipButton';

export const AccountMenuButton = React.memo(({ onLogout, compact = false }) => {
  const { isLightTheme } = useTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const [userEmail, setUserEmail] = React.useState(() => authManager.getState().user?.email || authManager.getCurrentUser()?.email || 'Signed in');
  const menuRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0 });

  const updateMenuPosition = React.useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 224;
    const viewportPadding = 12;
    const left = Math.min(
      Math.max(rect.right - menuWidth, viewportPadding),
      window.innerWidth - menuWidth - viewportPadding
    );

    setMenuPosition({
      top: rect.bottom + 8,
      left,
    });
  }, []);

  React.useEffect(() => {
    const unsubscribe = authManager.subscribe((state) => {
      setUserEmail(state.user?.email || authManager.getCurrentUser()?.email || 'Signed in');
    });

    return unsubscribe;
  }, []);

  React.useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();

    const handlePointerDown = (event) => {
      const clickedMenu = menuRef.current?.contains(event.target);
      const clickedButton = buttonRef.current?.contains(event.target);

      if (!clickedMenu && !clickedButton) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  const handleLogoutClick = () => {
    setIsOpen(false);
    onLogout();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Account menu"
        aria-expanded={isOpen}
        title="Account"
        className={`${compact ? 'h-7 w-7' : 'h-8 w-8'} flex items-center justify-center rounded-md transition-colors duration-150 ${
          isLightTheme
            ? `hover:bg-white/20 ${isOpen ? 'bg-white/30' : ''}`
            : `hover:bg-surface-dark ${isOpen ? 'bg-surface-dark' : ''}`
        }`}
      >
        <Power className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} rotate-180 ${isLightTheme ? 'text-white' : 'text-on-surface'}`} />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={`fixed w-56 rounded-xl border shadow-xl z-[12000] ${
            isLightTheme
              ? 'border-white/20 bg-white text-slate-900 shadow-black/15'
              : 'border-border-color bg-surface text-on-surface shadow-black/40'
          }`}
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
        >
          <div className={`px-3 py-3 border-b ${isLightTheme ? 'border-slate-200/80' : 'border-border-color'}`}>
            <div className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${isLightTheme ? 'text-slate-500' : 'text-on-surface-dim'}`}>
              Signed In As
            </div>
            <div className="mt-2 flex items-start gap-2">
              <Mail className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 ${isLightTheme ? 'text-slate-500' : 'text-on-surface-dim'}`} />
              <div className={`min-w-0 text-[12px] font-medium leading-relaxed break-all ${isLightTheme ? 'text-slate-800' : 'text-on-surface'}`}>
                {userEmail}
              </div>
            </div>
          </div>

          <div className="p-2">
            <button
              type="button"
              onClick={handleLogoutClick}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                isLightTheme
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-red-300 hover:bg-red-500/10'
              }`}
            >
              <Power className="h-4 w-4 rotate-180" />
              Logout
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

AccountMenuButton.displayName = 'AccountMenuButton';

/* ========================
 * HeaderActionButtons - Memoized component containing action buttons
 * Mobile-optimized to show only essential buttons on mobile devices
 * ========================*/
const HeaderActionButtons = React.memo(({
  isMemorySliderOpen,
  onMemorySliderToggle,
  sliderRef,
  onNewChatClick,
  onSnippetsClick,
  onThemeToggle,
  onLogout,
  isSnippetsOpen = false,
  isMobileView = false,
  hasNewCompletion,
  hasPendingApprovalTask,
  showLogout = true,
}) => {
  return (
    <>
      <Tooltip.Provider delayDuration={TOOLTIP_DELAY}>
        <div className="flex items-center gap-2 shrink-0">
          <TooltipButton onClick={() => onNewChatClick()} tooltipText="New Chat" icon={PlusIcon} />
          <div className="w-8 flex justify-center"><PruningSlider isOpen={isMemorySliderOpen} onToggle={onMemorySliderToggle} sliderRef={sliderRef} /></div>
          <TooltipButton onClick={onSnippetsClick} tooltipText="Snippets" icon={Bookmark} isActive={isSnippetsOpen} />
          <TooltipButton onClick={onThemeToggle} tooltipText="Toggle theme" icon={Palette} />
          {showLogout && <AccountMenuButton onLogout={onLogout} />}

          {!isMobileView && (
            <>
              <WorkspaceSwitcher
                hasNewCompletion={hasNewCompletion}
                hasPendingApprovalTask={hasPendingApprovalTask}
              />
            </>
          )}
        </div>
      </Tooltip.Provider>
    </>
  );
});

HeaderActionButtons.displayName = 'HeaderActionButtons';
export default HeaderActionButtons;